import React, {useEffect, useState} from 'react';
import {Autocomplete, Checkbox, Chip, FormControl, InputAdornment, TextField} from '@mui/material';
import {CheckBoxOutlineBlank, CheckBox, Close} from '@mui/icons-material';
import {PtrnAccionPametroAuxiliar, PtrnCampoEditableSelectCheckBox} from '../../../patronTypes';
import {EditableProps} from '../campoEditableTypes';
import {getNoBorderSx, useInputStyles} from '../campoEditableUtils';
import {PtrnPropsUtil} from '../../../patronUtils';

function CampoEditableSelectCheckbox(
   props: {
      campoConfig: PtrnCampoEditableSelectCheckBox;
   } & EditableProps & {
         ErrorComponent?: React.ReactNode;
      }
) {
   const {campoConfig, ...editableProps} = props;
   const auxParam: PtrnAccionPametroAuxiliar = {
      camposData: editableProps.camposData,
      campoConfig: campoConfig,
      ptrnRef: editableProps.ptrnRef,
      ruta: editableProps.ruta,
      index: editableProps.index
   };

   const classes = useInputStyles();

   const externalFilter = () => {
      if (campoConfig.array) {
         if (campoConfig.externalFilter) {
            return campoConfig.array.filter((value) => campoConfig.externalFilter(value, auxParam));
         } else {
            return campoConfig.array;
         }
      } else {
         return [];
      }
   };

   const labelItem = (campoValor: any): string => {
      if (campoConfig.generarDenominacionMostrar) {
         return campoConfig.generarDenominacionMostrar(campoValor, auxParam);
      }

      // Asegurarte de que sea un string o renderizable
      if (typeof campoValor === 'object') {
         return JSON.stringify(campoValor); // Convierte el objeto a string
      }
      return campoValor.toString(); // Convierte otros valores a string
   };

   const labelItemSelected = (campoValor: any): string => {
      if (campoConfig.generarValorMostrar) {
         return campoConfig.generarValorMostrar(campoValor, auxParam);
      }

      // Asegurarte de que sea un string o renderizable
      if (typeof campoValor === 'object') {
         return JSON.stringify(campoValor); // Convierte el objeto a string
      }
      return campoValor.toString(); // Convierte otros valores a string
   };

   const handleOnChange = (_event: React.SyntheticEvent, newValue: any | any[]) => {
      const name = campoConfig.name;
      const value = campoConfig.esIndividual
         ? newValue.length > 0
            ? [newValue[newValue.length - 1]] // Tomar solo el último valor
            : []
         : newValue || [];
      editableProps.onChange(name, value, auxParam);
   };

   const handleOnBlur: React.FocusEventHandler<HTMLDivElement> = (e) => {
      if (editableProps.onBlur) {
         const name = campoConfig.name;
         let newValue = editableProps.value;
         editableProps.onBlur(name, newValue, auxParam);
      }
   };

   const handleDelete = (itemToRemove: any) => {
      const updatedValue = (editableProps.value || []).filter(
         (item: any) => labelItemSelected(item) !== labelItemSelected(itemToRemove)
      );
      handleOnChange(null, updatedValue);
   };

   const [hasFocus, setHasFocus] = useState(false);

   const id = PtrnPropsUtil.genIdCampoEditable(campoConfig);
   const endAdornments = props.ErrorComponent ? (
      <InputAdornment position="end">{props.ErrorComponent}</InputAdornment>
   ) : undefined;

   const onClose = (event, reason) => {
      if (reason !== 'selectOption' && event.type !== 'mousedown') {
         setHasFocus(false);
      }
   };

   const InputSearch = (params: any) => {
      const {InputProps, inputProps, ...restParams} = params;

      return (
         <TextField
            {...restParams}
            InputProps={{
               ...InputProps,
               style: {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  minHeight: 40 // Evita que el `Autocomplete` cambie de altura
               },
               endAdornment: (
                  <>
                     {InputProps.endAdornment}
                     {endAdornments}
                  </>
               )
            }}
            inputProps={{
               ...inputProps,
               style: {
                  width: hasFocus ? 'auto' : '0px', // Oculta el input si no está en foco
                  minWidth: '0px', // Evita que afecte la disposición de los `Chip`
                  padding: hasFocus ? '2.5px 4px 2.5px 8px' : '0px', // Expande el input cuando está en foco
                  overflow: 'hidden'
               }
            }}
            label={campoConfig.label}
            error={Boolean(props.ErrorComponent)}
            size="small"
            onFocus={() => setHasFocus(true)} // Mantener el estado de foco
            onBlur={() => setHasFocus(false)} // Cerrar solo al perder foco
         />
      );
   };

   const filteredOptions = externalFilter();
   useEffect(() => {
      if (
         campoConfig.asignarPrimerValorSiNull &&
         (editableProps.value === null || editableProps.value.length === 0) &&
         filteredOptions.length > 0
      ) {
         editableProps.onChange(campoConfig.name, [filteredOptions[0]], auxParam);
      }
   }, [editableProps.value, campoConfig.array]);

   useEffect(() => {
      if (campoConfig.asignarPrimerValorTodos && filteredOptions.length > 0) {
         editableProps.onChange(campoConfig.name, filteredOptions, auxParam);
      }
   }, [campoConfig.array]);

   return (
      <FormControl fullWidth error={Boolean(props.ErrorComponent)}>
         <Autocomplete
            id={id}
            multiple // Siempre manejar como múltiple para mostrar checkboxes
            disableCloseOnSelect // No cerrar al seleccionar
            disableClearable={campoConfig.disableClearable}
            open={hasFocus}
            options={filteredOptions}
            getOptionLabel={labelItem}
            value={editableProps.value || []}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            sx={getNoBorderSx(campoConfig)}
            renderInput={InputSearch}
            isOptionEqualToValue={(option, value) => labelItem(option) === labelItem(value)}
            renderOption={(props, option) => (
               <li {...props}>
                  <Checkbox
                     icon={<CheckBoxOutlineBlank fontSize="small" />}
                     checkedIcon={<CheckBox fontSize="small" />}
                     style={{marginRight: 8}}
                     checked={(editableProps.value || []).some(
                        (item: any) => labelItemSelected(item) === labelItemSelected(option)
                     )}
                  />
                  {labelItem(option)}
               </li>
            )}
            noOptionsText={campoConfig.emptyMessage || 'No hay opciones disponibles'}
            renderTags={(tagValue, getTagProps) => {
               const maxVisibleChips = campoConfig.maxVisibleChips ?? 3;
               const visibleChips = hasFocus ? tagValue : tagValue.slice(0, maxVisibleChips);
               const extraChips = tagValue.length - maxVisibleChips;

               return (
                  <>
                     {visibleChips.map((option, index) => (
                        <Chip
                           key={labelItemSelected(option)}
                           label={labelItemSelected(option)}
                           {...getTagProps({index})}
                           onDelete={() => handleDelete(option)}
                           deleteIcon={<Close />}
                        />
                     ))}
                     {!hasFocus && extraChips > 0 && (
                        <Chip
                           label={`+${extraChips} más`}
                           disabled
                           style={{backgroundColor: '#f5f5f5', color: '#757575'}}
                        />
                     )}
                  </>
               );
            }}
         />
      </FormControl>
   );
}

export default CampoEditableSelectCheckbox;
